<template>
  <div class="question-bank-import">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>导入题库</h1>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/admin/question-banks">题库管理</router-link>
          </li>
          <li class="breadcrumb-item active">导入题库</li>
        </ol>
      </nav>
    </div>

    <!-- 上传区域 -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">上传题库文件</h5>
      </div>
      <div class="card-body">
        <div class="upload-area border rounded p-4 text-center"
          :class="{ 'border-primary': isDragging }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <div v-if="!file">
            <i class="bi bi-cloud-upload" style="font-size: 3rem; color: #6c757d;"></i>
            <h5 class="mt-3">拖拽文件到此处或点击上传</h5>
            <p class="text-muted">支持JSON、TXT和MD格式，文件大小不超过10MB</p>
            <input
              type="file"
              ref="fileInput"
              accept=".json,.txt,.md"
              class="d-none"
              @change="handleFileUpload"
            />
            <button type="button" class="btn btn-primary" @click="selectFile">
              <i class="bi bi-file-earmark-arrow-up me-2"></i>
              选择文件
            </button>
          </div>
          
          <div v-else class="file-info">
            <i 
              :class="{
                'bi-filetype-json': fileType === 'json',
                'bi-filetype-txt': fileName.toLowerCase().endsWith('.txt'),
                'bi-filetype-md': fileName.toLowerCase().endsWith('.md')
              }" 
              class="bi" 
              style="font-size: 2rem; color: #0d6efd;"
            ></i>
            <h5 class="mt-2">{{ fileName }}</h5>
            <p class="text-muted">{{ formatFileSize(file.size) }}</p>
            <div class="mt-3">
              <button type="button" class="btn btn-outline-danger btn-sm" @click="clearFile">
                <i class="bi bi-trash me-1"></i>
                重新选择
              </button>
            </div>
          </div>
        </div>

        <!-- 错误提示 -->
        <div v-if="error" class="alert alert-danger mt-3">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
      </div>
    </div>

<!-- 预览区域 -->
    <div v-if="fileContent" class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">题库预览</h5>
        <span class="badge bg-primary">共 {{ totalQuestions }} 道题目</span>
      </div>
      <div class="card-body">
        <!-- JSON格式预览 -->
        <div v-if="fileType === 'json'" class="row">
          <div class="col-md-12 mb-3">
            <h6>题库信息（可编辑）</h6>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label class="form-label">题库领域</label>
                <input 
                  v-model="fileContent.questionBankData.domain" 
                  class="form-control form-control-sm"
                  placeholder="如：职业卫生"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">题库子领域</label>
                <input 
                  v-model="fileContent.questionBankData.subdomain" 
                  class="form-control form-control-sm"
                  placeholder="如：职业病危害因素监测与检测"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">子领域代码</label>
                <input 
                  v-model="fileContent.questionBankData.subDomainCode" 
                  class="form-control form-control-sm"
                  placeholder="如：GBZ2.1-2019 或 职业卫生-001"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">章节</label>
                <input 
                  v-model="fileContent.questionBankData.chapter" 
                  class="form-control form-control-sm"
                  placeholder="如：第1章"
                />
              </div>
              <div class="col-md-12 mb-2">
                <label class="form-label">题库名称</label>
                <input 
                  v-model="fileContent.questionBankData.title" 
                  class="form-control form-control-sm"
                  placeholder="题库显示名称"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 文本格式预览 -->
        <div v-if="fileType === 'text'" class="row">
          <div class="col-md-12 mb-3">
            <h6>题库信息（可编辑）</h6>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label class="form-label">题库领域</label>
                <input 
                  v-model="fileContent.metadata.domain" 
                  class="form-control form-control-sm"
                  placeholder="如：职业卫生"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">题库子领域</label>
                <input 
                  v-model="fileContent.metadata.subdomain" 
                  class="form-control form-control-sm"
                  placeholder="如：职业病危害因素监测与检测"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">子领域代码</label>
                <input 
                  v-model="fileContent.metadata.subDomainCode" 
                  class="form-control form-control-sm"
                  placeholder="如：GBZ2.1-2019 或 职业卫生-001"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label">章节</label>
                <input 
                  v-model="fileContent.metadata.chapter" 
                  class="form-control form-control-sm"
                  placeholder="如：第1章"
                />
              </div>
              <div class="col-md-12 mb-2">
                <label class="form-label">题库名称</label>
                <input 
                  v-model="fileContent.metadata.title" 
                  class="form-control form-control-sm"
                  placeholder="题库显示名称"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 题目预览 -->
        <div class="mt-3">
          <h6>题目预览</h6>
          <div class="preview-list" style="max-height: 300px; overflow-y: auto;">
            <!-- JSON格式题目 -->
            <template v-if="fileType === 'json'">
              <div
                v-for="(question, index) in fileContent.questionBankData.quiz.slice(0, 3)"
                :key="index"
                class="border rounded p-2 mb-2"
              >
                <strong>{{ index + 1 }}. {{ question.question }}</strong>
                <div class="text-muted small">{{ question.chapter }} - {{ getQuestionTypeText(question.type) }}</div>
              </div>
            </template>
            
            <!-- 文本格式题目 -->
            <template v-if="fileType === 'text'">
              <div
                v-for="(question, index) in fileContent.questions.slice(0, 3)"
                :key="index"
                class="border rounded p-2 mb-2"
              >
                <strong>{{ index + 1 }}. {{ question.question }}</strong>
                <div class="text-muted small">{{ question.chapter }} - {{ getQuestionTypeText(question.type) }}</div>
              </div>
            </template>
            
            <p v-if="totalQuestions > 3" class="text-muted small">... 还有 {{ totalQuestions - 3 }} 道题目</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 导入选项 -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">导入选项</h5>
      </div>
      <div class="card-body">
        <!-- 现有题库选择 -->
        <div class="mb-3">
          <label class="form-label">选择导入方式</label>
          <div class="form-check">
            <input
              type="radio"
              id="new-bank"
              name="import-mode"
              class="form-check-input"
              v-model="importMode"
              value="new"
            />
            <label for="new-bank" class="form-check-label">
              创建新题库
            </label>
          </div>
          <div class="form-check">
            <input
              type="radio"
              id="existing-bank"
              name="import-mode"
              class="form-check-input"
              v-model="importMode"
              value="existing"
            />
            <label for="existing-bank" class="form-check-label">
              添加到现有题库
            </label>
          </div>
        </div>

        <!-- 现有题库选择下拉框 -->
        <div v-if="importMode === 'existing'" class="mb-3">
          <label for="existing-bank-select" class="form-label">选择现有题库</label>
          <select 
            id="existing-bank-select" 
            class="form-select" 
            v-model="selectedBankId"
            :disabled="loadingBanks"
          >
            <option value="">请选择题库...</option>
            <option 
              v-for="bank in existingBanks" 
              :key="bank.id" 
              :value="bank.id"
            >
              {{ bank.description || bank.subdomain }} ({{ bank.totalQuestions }}题)
            </option>
          </select>
          <div v-if="loadingBanks" class="text-muted small mt-1">
            <i class="bi bi-hourglass-split"></i> 加载题库列表中...
          </div>
        </div>

        <!-- 覆盖选项（仅创建新题库时显示） -->
        <div v-if="importMode === 'new'" class="form-check">
          <input
            type="checkbox"
            id="overwrite-toggle"
            class="form-check-input"
            v-model="overwrite"
          />
          <label for="overwrite-toggle" class="form-check-label">
            <strong class="text-warning">覆盖已有题库</strong>
            <br />
            <small class="text-muted">如果已存在相同代码的题库，将完全替换原有题库和题目</small>
          </label>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="d-flex justify-content-end gap-2">
      <router-link to="/admin/question-banks" class="btn btn-secondary">
        <i class="bi bi-arrow-left me-2"></i>
        返回
      </router-link>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!file || (!isValidJson && !isValidText) || importing || 
                 (importMode === 'existing' && !selectedBankId)"
        @click="handleImport"
      >
        <span v-if="importing" class="spinner-border spinner-border-sm me-2" role="status"></span>
        <i v-else class="bi bi-upload me-2"></i>
        {{ importing ? '导入中...' : (importMode === 'new' ? '创建新题库' : '添加到题库') }}
      </button>
    </div>

    <!-- 成功提示 -->
    <div v-if="successMessage" class="alert alert-success mt-3">
      <i class="bi bi-check-circle me-2"></i>
      {{ successMessage }}
      <div class="mt-2">
        <router-link :to="`/admin/question-banks/${importedBankId}`" class="btn btn-sm btn-success me-2">
          <i class="bi bi-eye me-1"></i>
          查看题库
        </router-link>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="resetForm">
          <i class="bi bi-arrow-clockwise me-1"></i>
          开始新的导入
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { questionBanksApi } from '@/api/questionBanks'
import { getQuestionTypeText } from '@/utils/questionTypes'

const router = useRouter()
const toast = useToast()

// 状态
const file = ref<File | null>(null)
const fileName = ref('')
const fileContent = ref<any>(null)
const isValidJson = ref(false)
const isValidText = ref(false)
const fileType = ref<'json' | 'text' | null>(null)
const error = ref('')
const overwrite = ref(false)
const importing = ref(false)
const successMessage = ref('')
const importedBankId = ref<number | null>(null)
const isDragging = ref(false)
const importMode = ref<'new' | 'existing'>('new')
const selectedBankId = ref<number | string>('')
const existingBanks = ref<any[]>([])
const loadingBanks = ref(false)

// 双引号处理工具函数
const safeJsonParse = (jsonString: string) => {
  try {
    // 首先尝试直接解析
    return JSON.parse(jsonString)
  } catch (error) {
    try {
      // 如果失败，尝试修复常见的引号问题
      const fixed = jsonString
        // 将中文双引号替换为英文双引号（在JSON值中）
        .replace(/"([^"]*)"/g, '"$1"')
        // 确保属性名使用英文双引号
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
      
      return JSON.parse(fixed)
    } catch (secondError) {
      console.error('JSON解析失败:', secondError)
      throw new Error('JSON格式不正确，请检查引号格式')
    }
  }
}

// 题目文本验证函数
const validateQuestionText = (text: string) => {
  const issues = []
  
  // 检查引号配对
  const doubleQuoteCount = (text.match(/"/g) || []).length
  if (doubleQuoteCount % 2 !== 0) {
    issues.push('双引号未配对')
  }
  
  // 检查中英文引号混用
  const hasChineseQuotes = /[""]/g.test(text)
  const hasEnglishQuotes = /"/g.test(text)
  
  if (hasChineseQuotes && hasEnglishQuotes) {
    issues.push('使用了英文双引号')
  }
  
  return {
    valid: issues.length === 0,
    issues
  }
}

// 计算属性
const totalQuestions = computed(() => {
  if (fileType.value === 'json') {
    return fileContent.value?.questionBankData?.quiz?.length || 0
  } else if (fileType.value === 'text') {
    return fileContent.value?.questions?.length || 0
  }
  return 0
})

// 方法
const resetForm = () => {
  file.value = null
  fileName.value = ''
  fileContent.value = null
  isValidJson.value = false
  isValidText.value = false
  fileType.value = null
  error.value = ''
  successMessage.value = ''
  importedBankId.value = null
  overwrite.value = false
  importMode.value = 'new'
  selectedBankId.value = ''
  
  // 清空文件输入
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }
}

const selectFile = () => {
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
  fileInput?.click()
}

const handleDragOver = () => {
  isDragging.value = true
}

const handleDragLeave = () => {
  isDragging.value = false
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    processFile(files[0])
  }
}

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    processFile(files[0])
  }
}

const processFile = (selectedFile: File) => {
  // 重置状态
  error.value = ''
  fileContent.value = null
  isValidJson.value = false
  isValidText.value = false
  fileType.value = null

  // 验证文件大小 (10MB)
  if (selectedFile.size > 10 * 1024 * 1024) {
    error.value = '文件大小不能超过10MB'
    return
  }

  file.value = selectedFile
  fileName.value = selectedFile.name

  // 读取文件内容
  const reader = new FileReader()
  reader.onload = (e) => {
    const content = e.target?.result as string
    const fileExtension = selectedFile.name.toLowerCase().split('.').pop()

    try {
      if (fileExtension === 'json') {
        // 处理JSON文件 - 使用安全的JSON解析
        const jsonContent = safeJsonParse(content)
        
        // 验证JSON结构 - 支持两种格式
        let normalizedContent: any
        if (jsonContent.questionBankData) {
          // 原格式：包含questionBankData字段
          if (!jsonContent.questionBankData.domain) {
            throw new Error('JSON格式不符合要求：缺少domain字段')
          }
          // 确保title字段存在
          if (!jsonContent.questionBankData.title) {
            jsonContent.questionBankData.title = jsonContent.questionBankData.subdomain || '新题库'
          }
          normalizedContent = jsonContent
        } else if (jsonContent.domain && jsonContent.subdomain && jsonContent.quiz) {
          // 新格式：直接使用顶层字段
          normalizedContent = {
            questionBankData: {
              domain: jsonContent.domain,
              subdomain: jsonContent.subdomain,
              subDomainCode: jsonContent.subDomainCode || 'UNKNOWN',
              title: jsonContent.title || jsonContent.subdomain || '新题库',
              chapter: jsonContent.chapter || '第1章',
              quiz: jsonContent.quiz
            }
          }
        } else {
          throw new Error('JSON格式不符合要求：缺少必要的题库字段')
        }
        
        fileContent.value = normalizedContent
        isValidJson.value = true
        fileType.value = 'json'
        error.value = ''
      } else if (fileExtension === 'txt' || fileExtension === 'md') {
        // 处理文本文件（包括TXT和MD格式）
        const parsedContent = parseTextContent(content)
        fileContent.value = parsedContent
        isValidText.value = true
        fileType.value = 'text'
        error.value = ''
      } else {
        error.value = '请上传.txt、.md或.json格式的文件'
      }
    } catch (err: any) {
      error.value = err.message || '文件格式解析失败'
      file.value = null
      fileName.value = ''
    }
  }
  reader.readAsText(selectedFile)
}

const clearFile = () => {
  // 只清除文件相关状态，保留成功消息
  file.value = null
  fileName.value = ''
  fileContent.value = null
  isValidJson.value = false
  isValidText.value = false
  fileType.value = null
  error.value = ''
  // 不清除 successMessage 和 importedBankId，让用户可以看到反馈
  
  // 清空文件输入
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// getQuestionTypeText 函数已从 @/utils/questionTypes 导入

const loadExistingBanks = async () => {
  loadingBanks.value = true
  try {
    const response = await questionBanksApi.getQuestionBanks()
    existingBanks.value = response.data || []
  } catch (err) {
    toast.error('加载题库列表失败')
    console.error('Failed to load banks:', err)
  } finally {
    loadingBanks.value = false
  }
}

const handleImport = async () => {
  if (!file.value || (!isValidJson.value && !isValidText.value)) {
    error.value = '请先选择有效的文件'
    return
  }

  if (importMode.value === 'existing' && !selectedBankId.value) {
    error.value = '请选择一个现有题库'
    return
  }

  importing.value = true
  error.value = ''

  try {
    let result: any
    
    if (importMode.value === 'new') {
      // 创建新题库
      if (fileType.value === 'json') {
        // 使用表单中编辑的值来更新JSON内容
        const updatedContent = {
          ...fileContent.value,
          questionBankData: {
            ...fileContent.value.questionBankData,
            domain: fileContent.value.questionBankData.domain,
            subdomain: fileContent.value.questionBankData.subdomain,
            subDomainCode: fileContent.value.questionBankData.subDomainCode,
            title: fileContent.value.questionBankData.title,
            chapter: fileContent.value.questionBankData.chapter,
            quiz: fileContent.value.questionBankData.quiz.map((q: any) => ({
              ...q,
              chapter: fileContent.value.questionBankData.chapter || q.chapter || '第1章'
            }))
          }
        }
        
        // 创建新的JSON文件并上传
        const updatedBlob = new Blob([JSON.stringify(updatedContent, null, 2)], { type: 'application/json' })
        const updatedFile = new File([updatedBlob], fileName.value, { type: 'application/json' })
        
        result = await questionBanksApi.importQuestionBankFromFile(
          updatedFile,
          overwrite.value
        )
      } else if (fileType.value === 'text') {
        // 使用UI表单中的章节信息更新所有题目
        const updatedQuestions = fileContent.value.questions.map((q: any) => ({
          ...q,
          chapter: fileContent.value.metadata.chapter || q.chapter || '第1章'
        }))
        
        const importData = {
          domain: fileContent.value.metadata.domain,
          subdomain: fileContent.value.metadata.subdomain,
          subDomainCode: fileContent.value.metadata.subDomainCode,
          title: fileContent.value.metadata.title,
          questions: updatedQuestions
        }
        result = await questionBanksApi.importQuestionBankFromText(importData, overwrite.value)
      }
    } else {
      // 添加到现有题库
      const questions = fileType.value === 'json' 
        ? fileContent.value.questionBankData.quiz 
        : fileContent.value.questions
      
      const formattedQuestions = questions.map((q: any) => ({
        type: q.type || q.questionType, // 支持两种字段名：type 和 questionType
        chapter: fileType.value === 'json' 
          ? (fileContent.value.questionBankData.chapter || q.chapter || '第1章')
          : (fileContent.value.metadata.chapter || q.chapter || '第1章'),
        question: q.question,
        options: q.options || {},
        answer: Array.isArray(q.answer) ? q.answer.join(',') : q.answer,
        explanation: q.explanation || '',
        difficulty: q.difficulty || 1 // 添加difficulty字段，确保从原始数据中获取难度值
      }))
      
      await questionBanksApi.addQuestionsToBank(Number(selectedBankId.value), formattedQuestions)
      
      // 获取题库信息用于成功消息
      const bank = await questionBanksApi.getQuestionBank(Number(selectedBankId.value))
      result = bank
    }
    
    importedBankId.value = result.id
    const totalQuestions = result.questions?.length || 
      (fileType.value === 'json' ? fileContent.value.questionBankData.quiz.length : fileContent.value.questions.length)
    
    const action = importMode.value === 'new' ? '导入' : '添加到'
    successMessage.value = `题目${action}题库 "${result.subdomain}" 成功！共${action} ${totalQuestions} 道题目。`
    toast.success(`题目${action}成功！共${totalQuestions}题`)    
    
    // 只清除文件输入，保留成功反馈
    file.value = null
    fileName.value = ''
    // 不清除其他状态，让用户可以看到成功信息和预览
  } catch (err: any) {
    // Handle 409 conflict specifically
    if (err.response?.status === 409) {
      error.value = err.response?.data?.message || '题库已存在，可以选择覆盖导入'
    } else {
      error.value = err.message || '导入失败，请检查文件内容'
    }
    toast.error(error.value)
  } finally {
    importing.value = false
  }
}

// 纯文本题库解析 - 使用后端修复后的解析逻辑
const parseTextContent = (content: string) => {
  // 提取题库元数据
  const metadata = {
    title: extractTitle(content),
    chapter: extractChapter(content),
    domain: '职业卫生',
    subdomain: extractSubdomain(content),
    subDomainCode: generateSubdomainCode(content)
  };
  
  // 使用默认值确保不会缺少必需字段
  const finalMetadata = {
    title: metadata.title || '职业病危害因素题库',
    chapter: metadata.chapter || '第1章',
    domain: metadata.domain || '职业卫生',
    subdomain: metadata.subdomain || '职业病危害因素监测与检测',
    subDomainCode: metadata.subDomainCode || 'OCCUPATION_MONITORING_001'
  };

  // 不再需要基于文件名推断题型，改为对每个题目单独智能识别
  // 使用修复后的解析逻辑
  const questions = parseTextWithCorrectedLogic(content, finalMetadata);
  
  return {
    metadata: finalMetadata,
    questions
  };
};

// 智能题目类型识别函数
const detectQuestionType = (question: any, answer: string) => {
  // 1. 基于选项数量和内容判断
  if (question.options) {
    const optionCount = Object.keys(question.options).length;
    const optionValues = Object.values(question.options) as string[];
    
    // 判断题特征：只有2个选项且包含对错、是否、正确错误等关键词
    if (optionCount === 2) {
      const combinedOptions = optionValues.join('').toLowerCase();
      if (combinedOptions.includes('正确') && combinedOptions.includes('错误') ||
          combinedOptions.includes('对') && combinedOptions.includes('错') ||
          combinedOptions.includes('是') && combinedOptions.includes('否') ||
          combinedOptions.includes('√') && combinedOptions.includes('×')) {
        return 'trueFalse';
      }
    }
    
    // 多选题特征：答案包含多个字母或选项数量较多
    if (optionCount > 2) {
      const answerLetters = answer.toUpperCase().match(/[A-E]/g) || [];
      if (answerLetters.length > 1) {
        return 'multipleChoice';
      }
      
      // 如果选项数量大于4，且题目包含多选关键词，也可能是多选题
      if (optionCount >= 4) {
        const questionText = question.text.toLowerCase();
        if (questionText.includes('多选') || questionText.includes('选择正确的') ||
            questionText.includes('以下正确的') || questionText.includes('包括') ||
            questionText.includes('哪些') || questionText.includes('都有')) {
          return 'multipleChoice';
        }
      }
    }
    
    // 单选题：有选项但不是判断题或多选题
    if (optionCount > 0) {
      return 'singleChoice';
    }
  }
  
  // 2. 基于题目内容判断
  const questionText = question.text.toLowerCase();
  
  // 判断题特征：题目中包含判断性词汇
  if (questionText.includes('正确') || questionText.includes('错误') ||
      questionText.includes('对吗') || questionText.includes('错吗') ||
      questionText.includes('是否') || questionText.includes('能否') ||
      questionText.includes('判断') || questionText.includes('对错') ||
      questionText.includes('真假') || questionText.includes('是非') ||
      questionText.includes('符合') || questionText.includes('不符合') ||
      questionText.includes('属于') || questionText.includes('不属于')) {
    return 'trueFalse';
  }
  
  // 简答题特征：包含简答关键词
  if (questionText.includes('简述') || questionText.includes('说明') ||
      questionText.includes('阐述') || questionText.includes('分析')) {
    return 'essay';
  }
  
  // 计算题特征：包含计算关键词
  if (questionText.includes('计算') || questionText.includes('求') ||
      questionText.includes('公式') || questionText.includes('数值')) {
    return 'calculation';
  }
  
  // 3. 基于答案格式判断
  const normalizedAnswer = answer.toLowerCase().trim();
  
  // 判断题答案特征
  if (normalizedAnswer === '√' || normalizedAnswer === '×' ||
      normalizedAnswer === '正确' || normalizedAnswer === '错误' ||
      normalizedAnswer === '对' || normalizedAnswer === '错' ||
      normalizedAnswer === '是' || normalizedAnswer === '否') {
    return 'trueFalse';
  }
  
  // 多选题答案特征：多个字母
  const answerLetters = answer.toUpperCase().match(/[A-E]/g) || [];
  if (answerLetters.length > 1) {
    return 'multipleChoice';
  }
  
  // 默认为单选题
  return 'singleChoice';
};

// 使用修复后的文本解析逻辑
const parseTextWithCorrectedLogic = (content: string, metadata: any) => {
  const lines = content.split('\n').map(line => line.trim()).filter(line => line);
  const questions: any[] = [];

  // 查找题目和答案的位置
  let questionStart = -1;
  let answerStart = -1;
  
  // 查找题目开始（跳过标题行）
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].match(/^\d+[\.、]\s*.+/)) {
      questionStart = i;
      break;
    }
  }
  
  if (questionStart === -1) questionStart = 1;

  // 查找答案开始
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes('答案') && !line.match(/^\d+[\.、]/)) {
      answerStart = i + 1;
      break;
    }
  }

  if (answerStart === -1) {
    // 如果没找到答案开始，从文件末尾开始找
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].match(/^\d+[\.、]\s*[A-Za-z]/)) {
        answerStart = i;
        break;
      }
    }
  }

  if (answerStart === -1) {
    answerStart = lines.length;
  }

  // 解析题目
  const questionLines = lines.slice(questionStart, answerStart - 1);
  const rawQuestions: any[] = [];
  let currentQuestion: any = null;

  for (const line of questionLines) {
    const questionMatch = line.match(/^(\d+)[\.、]\s*(.+)$/);
    
    if (questionMatch) {
      // 保存上一个题目
      if (currentQuestion) {
        rawQuestions.push(currentQuestion);
      }
      
      // 开始新题目
      currentQuestion = {
        number: parseInt(questionMatch[1]),
        text: questionMatch[2],
        options: {} // 默认添加选项对象，后续根据实际情况使用
      };
    } else if (currentQuestion) {
      // 处理选项或多行题目
      const optionMatch = line.match(/^([A-E])[\.、]\s*(.+)$/);
      if (optionMatch && currentQuestion.options) {
        currentQuestion.options[optionMatch[1]] = optionMatch[2].trim();
      } else if (line.trim() && !line.match(/^\d+[\.、]/)) {
        // 多行题目内容
        if (!currentQuestion.options || Object.keys(currentQuestion.options).length === 0) {
          currentQuestion.text += ' ' + line.trim();
        }
      }
    }
  }

  // 添加最后一个题目
  if (currentQuestion) {
    rawQuestions.push(currentQuestion);
  }

  // 解析答案
  const answers: Record<number, string> = {};
  const answerLines = lines.slice(answerStart - 1);
  
  for (const line of answerLines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    // 解析答案格式：数字. 答案内容
    const answerMatch = trimmedLine.match(/^(\d+)[\.、]\s*(.+)$/);
    if (answerMatch) {
      const number = parseInt(answerMatch[1]);
      const answer = answerMatch[2].trim();
      answers[number] = answer;
    }
  }

  // 匹配题目和答案，智能识别题目类型
  for (const question of rawQuestions) {
    const answer = answers[question.number];
    if (answer) {
      // 使用智能识别函数确定题目类型
      const detectedType = detectQuestionType(question, answer);
      let finalAnswer: string | string[] = answer;
      
      // 根据识别的题型格式化答案
      if (detectedType === 'multipleChoice') {
        // 多选题答案可能是"ABC"格式，转为数组
        finalAnswer = answer.toUpperCase().split('').filter(c => /^[A-E]$/.test(c));
      } else if (detectedType === 'trueFalse') {
        // 判断题答案标准化
        const normalized = answer.toLowerCase();
        if (normalized.includes('正确') || normalized.includes('对') || normalized === '√' || normalized === '是') {
          finalAnswer = '√';
        } else if (normalized.includes('错误') || normalized.includes('错') || normalized === '×' || normalized === '否') {
          finalAnswer = '×';
        }
      } else if (detectedType === 'singleChoice') {
        // 单选题只取第一个字母
        const match = answer.toUpperCase().match(/^[A-E]/);
        finalAnswer = match ? match[0] : answer.toUpperCase();
      } else {
        // 简答题、计算题等保持原答案
        finalAnswer = answer;
      }

      // 清理空的选项对象（对于非选择题）
      const finalOptions = (detectedType === 'singleChoice' || detectedType === 'multipleChoice' || detectedType === 'trueFalse') 
        ? question.options 
        : undefined;

      questions.push({
        type: detectedType,
        chapter: metadata.chapter,
        question: question.text.trim(),
        options: finalOptions,
        answer: finalAnswer,
        explanation: ''
      });
    }
  }

  return questions;
};

// 辅助函数 - 优化中文文本解析
const extractTitle = (content: string) => {
  const titleMatch = content.match(/^(.+?)题库/);
  return titleMatch ? titleMatch[1].trim() : '职业病危害因素监测与检测题库';
};

const extractChapter = (content: string) => {
  const chapterMatch = content.match(/第(\d+)章/);
  return chapterMatch ? `第${chapterMatch[1]}章` : '第1章';
};

const extractSubdomain = (content: string) => {
  const titleMatch = content.match(/(.+?)题库/);
  if (titleMatch) {
    return titleMatch[1].trim();
  }
  // 从文件内容提取子领域
  if (content.includes('职业病危害因素监测与检测')) {
    return '职业病危害因素监测与检测';
  }
  return '职业病危害因素监测';
};

const generateSubdomainCode = (content: string) => {
  const subdomain = extractSubdomain(content);
  // 生成规范的代码
  const codeMap = {
    '职业病危害因素监测与检测': 'OCCUPATION_MONITORING_001',
    '职业病危害因素监测': 'OCCUPATION_MONITORING_001',
    '职业病危害': 'OCCUPATION_HAZARD_001',
    '职业卫生': 'OCCUPATION_HEALTH_001'
  };
  
  for (const key in codeMap) {
    if (subdomain.includes(key)) {
      return codeMap[key as keyof typeof codeMap];
    }
  }
  
  // 默认生成代码
  return 'OCCUPATION_DEFAULT_001';
};

const formatAnswer = (answer: string, type: string) => {
  if (!answer) return '';
  
  // 清理答案中的空格和标点
  const cleanAnswer = answer.trim().replace(/\s+/g, '');
  
  // 处理判断题
  if (type === 'trueFalse') {
    // 支持多种判断题答案格式
    if (cleanAnswer.includes('正确') || cleanAnswer.includes('对') || cleanAnswer === '√' || cleanAnswer.toUpperCase() === 'A') {
      return '正确';
    }
    if (cleanAnswer.includes('错误') || cleanAnswer.includes('错') || cleanAnswer === '×' || cleanAnswer.toUpperCase() === 'B') {
      return '错误';
    }
    return cleanAnswer;
  }
  
  if (type === 'multipleChoice') {
    // 处理多选题，提取字母并去重
    const matches = cleanAnswer.match(/[A-Z]+/g);
    if (matches) {
      const letters = matches.join('').split('').filter((v, i, a) => a.indexOf(v) === i);
      return letters.sort().join(',');
    }
    return cleanAnswer.replace(/[^A-Z,]/g, '');
  }
  
  // 单选题只取第一个字母或完整答案
  if (type === 'singleChoice') {
    const match = cleanAnswer.match(/^[A-Z]/);
    if (match) {
      return match[0];
    }
    // 支持中文答案
    if (cleanAnswer.length === 1) {
      return cleanAnswer.toUpperCase();
    }
    // 提取可能的字母答案
    const letterMatch = cleanAnswer.match(/[A-Z]/);
    return letterMatch ? letterMatch[0] : cleanAnswer;
  }
  
  // 对于填空题和简答题，返回原始答案
  return answer.trim();
};

// 监听导入模式变化
watch(importMode, (newMode) => {
  if (newMode === 'existing' && existingBanks.value.length === 0) {
    loadExistingBanks()
  }
})

</script>

<style scoped>
.question-bank-import {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.upload-area {
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-area:hover {
  background-color: #f8f9fa;
}

.preview-list {
  border-left: 3px solid #0d6efd;
  padding-left: 1rem;
}

.breadcrumb {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .question-bank-import {
    padding: 1rem;
  }
}
</style>